import React, { useState } from 'react';
import { Users, Search, FileText, TvMinimal, User } from 'lucide-react';
import { getAdminId, getRoles } from '../../utils/tokenUtils';
import { useNavigate } from 'react-router-dom';
import PerfectScrollbar from 'react-perfect-scrollbar';
import 'react-perfect-scrollbar/dist/css/styles.css';
import SimpleAlert from '../common/SimpleAlert';

interface SidebarProps {
  isOpen: boolean;
  activePage: string;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, activePage }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const roles = getRoles() || [];
  const navigate = useNavigate();
  const adminId = getAdminId();

  const handleNavigation = (page: string) => {
    navigate(page);
  };

  const menuGroups = [
    ...(roles.includes('Administrator')
      ? [
        {
          group: '',
          items: [
            {
              label: 'Dashboard',
              icon: <TvMinimal className="w-5 h-5" />,
              route: '/admin/dashboard',
            },
          ],
        },
        {
          group: 'Control Panel',
          items: [
            {
              label: 'Clients',
              icon: <FileText className="w-5 h-5" />,
              route: '/admin/clients',
            },
            {
              label: 'Leads',
              icon: <User className="w-5 h-5" />,
              route: '/admin/leads',
            },
            {
              label: 'Users',
              icon: <Users className="w-5 h-5" />,
              route: '/admin/users',
            },
          ],
        },
        {
          group: 'Settings',
          items: [
            {
              label: 'Audit Logs',
              icon: <FileText className="w-5 h-5" />,
              route: '/admin/auditlogs',
            },
            {
              label: 'Company Info',
              icon: <FileText className="w-5 h-5" />,
              route: '/admin/companyinfo',
            },
          ],
        },
      ]
      : []),

    ...(roles.includes('User')
      ? [
        {
          group: '',
          items: [
            {
              label: 'Dashboard',
              icon: <TvMinimal className="w-5 h-5" />,
              route: '/admin/dashboard',
            },
            {
              label: 'Leads',
              icon: <User className="w-5 h-5" />,
              route: '/admin/leads',
            },
          ],
        },
      ]
      : []),
  ];

  const filteredGroups = menuGroups.map((group) => ({
    ...group,
    items: group.items.filter((item) =>
      item.label.toLowerCase().includes(searchTerm.toLowerCase()),
    ),
  }));

  return (
    <aside
      id="nav-menu"
      aria-label="Side navigation"
      className={`fixed lg:static z-40 w-72 bg-gray-800 border-r border-gray-700 h-[calc(100%-4rem)] lg:h-full transition-transform ${isOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0 mt-12 lg:mt-0 flex flex-col`}
    >
      {/* Search Box */}
      <div className="relative my-6 px-4">
        <input
          type="search"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search here"
          aria-label="Search content"
          className="relative w-full h-10 px-4 pr-12 text-sm transition-all border rounded outline-none 
            border-gray-600 bg-gray-800 text-gray-300 autofill:bg-gray-800 
            focus:border-emerald-500 focus:outline-none 
            disabled:cursor-not-allowed disabled:bg-gray-700 disabled:text-gray-500"
        />
        <Search
          className="absolute top-2.5 right-7 h-5 w-5 cursor-pointer stroke-gray-300"
          aria-hidden="true"
          aria-label="Search icon"
          role="graphics-symbol"
        />
      </div>

      {/* Navigation Links */}
      <PerfectScrollbar
        options={{ suppressScrollX: true }}
        className="flex-1 overflow-hidden"
      >
        <div className="pl-6">
          {filteredGroups.map((group, index) => (
            <div key={index} className="mb-4">
              <h2 className="px-4 py-2 text-xs font-semibold text-gray-400 uppercase">
                {group.group}
              </h2>
              <ul className="space-y-2 px-4">
                {group.items.map((item, idx) => (
                  <li key={idx}>
                    <a
                      onClick={() => handleNavigation(item.route)}
                      className={`flex items-center gap-3 p-3 rounded transition text-sm cursor-pointer ${activePage.includes(item.label)
                        ? 'bg-gray-600 text-white'
                        : 'hover:bg-gray-600 hover:text-white text-white'
                        }`}
                    >
                      {item.icon}
                      <span>{item.label}</span>
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </PerfectScrollbar>
      {adminId && (
        <SimpleAlert message="Logged in as member" variant="success" />
      )}

      {/* Footer */}
      <footer className="border-t border-gray-700 p-4 mt-auto">
        <p className="text-sm text-gray-400">
          &copy; {new Date().getFullYear()} VSP. All rights reserved. Developed
          by Latreia.
        </p>
      </footer>
    </aside>
  );
};

export default Sidebar;
